import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, PreRegisteredEmailError } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { getPasswordResetRedirectUrl } from '@/lib/password-reset';
import { translateAuthError } from '@/lib/auth-errors';
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_HINT, validatePasswordPolicy } from '@/lib/password-policy';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [isSignUp, setIsSignUp] = useState(tabParam === 'signup');

  useEffect(() => {
    setIsSignUp(tabParam === 'signup');
  }, [tabParam]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn, signUp, user, isAdmin, isStaff, loading: authLoading } = useAuth();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim().toLowerCase(),
        { redirectTo: getPasswordResetRedirectUrl() },
      );
      if (error) throw error;
      toast.success('Se o e-mail estiver cadastrado, enviamos um link de recuperação. Verifique sua caixa de entrada e o spam.');
      setForgotOpen(false);
      setResetEmail('');
    } catch (err: any) {
      toast.error(translateAuthError(err, 'Erro ao enviar e-mail de recuperação.'));
    } finally {
      setResetLoading(false);
    }
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || authLoading) return;
    navigate((isAdmin || isStaff) ? '/admin' : '/booking');
  }, [user, isAdmin, isStaff, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignUp) {
        const policyError = validatePasswordPolicy(password);
        if (policyError) {
          toast.error(policyError);
          setLoading(false);
          return;
        }
        await signUp(email, password, fullName, phone);
        toast.success('Conta criada! Verifique seu email para confirmar.');
      } else {
        await signIn(email, password);
        toast.success('Bem-vinda de volta!');
        // Redirecionamento por papel é feito pelo useEffect acima após carregar isAdmin/isStaff
      }
    } catch (err: any) {
      if (err instanceof PreRegisteredEmailError) {
        toast.success(
          'Seu email já está cadastrado. Enviamos um link para você definir sua senha. Verifique sua caixa de entrada (e o spam).',
          { duration: 8000 },
        );
        setIsSignUp(false);
      } else {
        toast.error(translateAuthError(err, 'Erro ao autenticar.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-secondary via-background to-rose-light">
      <SEO
        title={isSignUp ? 'Criar Conta — Bonjour Sobrancelhas' : 'Entrar — Bonjour Sobrancelhas'}
        description="Acesse sua conta para agendar e gerenciar atendimentos na Bonjour Sobrancelhas."
        path="/auth"
        noindex
      />
      <Navbar />
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl text-primary">
              {isSignUp ? 'Criar Conta' : 'Entrar'}
            </CardTitle>
            <CardDescription>
              {isSignUp ? 'Crie sua conta para agendar' : 'Acesse sua conta'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <>
              <div>
                <Label htmlFor="fullName">Nome Completo</Label>
                <Input id="fullName" value={fullName} onChange={e => setFullName(e.target.value)} required placeholder="Seu nome" className="min-h-[44px]" />
              </div>
              <div>
                <Label htmlFor="phone">Telefone / WhatsApp</Label>
                <Input id="phone" value={phone} onChange={e => setPhone(e.target.value)} required placeholder="(11) 99999-9999" className="min-h-[44px]" />
              </div>
                </>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="seu@email.com" className="min-h-[44px]" />
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={isSignUp ? PASSWORD_MIN_LENGTH : 6} placeholder={isSignUp ? `Mínimo ${PASSWORD_MIN_LENGTH} caracteres` : 'Sua senha'} className="min-h-[44px]" />
                {isSignUp && (
                  <p className="mt-1 text-xs text-muted-foreground">{PASSWORD_POLICY_HINT}</p>
                )}
              </div>
              <Button type="submit" variant="hero" className="w-full min-h-[48px]" disabled={loading}>
                {loading ? 'Aguarde...' : isSignUp ? 'Cadastrar' : 'Entrar'}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              {isSignUp ? 'Já tem conta?' : 'Não tem conta?'}{' '}
              <button onClick={() => setIsSignUp(!isSignUp)} className="text-primary font-medium hover:underline">
                {isSignUp ? 'Entrar' : 'Cadastrar'}
              </button>
            </p>
            {!isSignUp && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                <button onClick={() => setForgotOpen(true)} className="text-primary font-medium hover:underline">
                  Esqueceu sua senha?
                </button>
              </p>
            )}

            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogContent className="max-w-[90vw] sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-primary">Recuperar Senha</DialogTitle>
                  <DialogDescription>
                    Informe seu email e enviaremos um link para redefinir sua senha.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label htmlFor="resetEmail">Email</Label>
                    <Input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      required
                      placeholder="seu@email.com"
                    />
                  </div>
                  <Button type="submit" variant="hero" className="w-full" disabled={resetLoading}>
                    {resetLoading ? 'Enviando...' : 'Enviar Link de Recuperação'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
